import { Box } from '@mui/material';
import { Button } from 'system/atoms';
import { FormField } from 'common/ui';
import { Footer } from 'components/Footer';
import React, { FC, useEffect } from 'react';
import { useFormik } from 'formik';
import { TrackingEventName } from 'services/tracking/constants';
import { ToastType, useToast } from 'services/toast';
import { getUserContactEmail } from 'services/user/utils/getUserContactEmail';
import { useUpdateUserMutation } from 'generated-graphql/hooks/apollo-hooks';
import { Paths } from 'services/navigation/paths';
import { useRecoilState } from 'recoil';
import { userAtom } from 'services/user/atoms';
import { useNavigate } from 'react-router-dom';
import { useTracking } from 'services/tracking/hooks';
import * as Yup from 'yup';
import { useUserFetcher } from 'services/user/hooks';

type FormValues = {
  name: string;
  phoneNumber: string | undefined;
  correspondenceEmail: string | undefined;
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  jobTitle: Yup.string(),
  phoneNumber: Yup.string(),
  correspondenceEmail: Yup.string().email('Invalid email')
});

type Props = {
  isOnboardingPage: boolean;
};

export const ProfileDetailsTab: FC<Props> = ({ isOnboardingPage }) => {
  const { refetchUser } = useUserFetcher();
  const [user, setUser] = useRecoilState(userAtom);

  const { showToast } = useToast();
  const navigate = useNavigate();
  const { track } = useTracking();

  const [updateUserMutation] = useUpdateUserMutation();

  const initialValues = {
    name: user?.name || '',
    phoneNumber: user?.phoneNumber || undefined,
    correspondenceEmail: user?.correspondenceEmail || user?.email || ''
  };

  const handleNavigation = () => {
    if (isOnboardingPage) {
      return navigate(Paths.HOME);
    }
  };

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const { name, phoneNumber, correspondenceEmail } = values;
      if (user?.id) {
        try {
          const isOnboard = isOnboardingPage ? true : undefined;

          if (user?.correspondenceEmail !== correspondenceEmail) {
            track(TrackingEventName.ProfileCorrespondenceEmailTypeChange);
          }

          const result = await updateUserMutation({
            variables: {
              input: {
                id: user?.id,
                name,
                isOnboard,
                phoneNumber,
                correspondenceEmail
              }
            }
          });

          setUser({
            ...user,
            ...result.data?.updateUser
          });

          showToast({
            message: 'Profile successfully updated',
            type: ToastType.Success
          });
          refetchUser(user.id);
          handleNavigation();
        } catch (error) {
          showToast({
            message: 'Could not update your data',
            type: ToastType.Error
          });
        } finally {
          setSubmitting(false);
        }
      } else {
        showToast({
          message: 'Could not update your data',
          type: ToastType.Error
        });
      }
    }
  });

  useEffect(() => {
    if (user?.id) {
      const correspondenceEmailDefaultValue = getUserContactEmail(user);

      formik.setValues({
        ...formik.values,
        name: user?.name || '',
        phoneNumber: user?.phoneNumber || undefined,
        correspondenceEmail: correspondenceEmailDefaultValue
      });
    }
  }, [user?.id]);

  return (
    <>
      <Box maxWidth={1000}>
        <form onSubmit={formik.handleSubmit}>
          <FormField
            title="Your details"
            subTitle="Check your details and amend if necessary"
            textFields={[
              {
                label: 'Full name',
                props: {
                  id: 'name',
                  name: 'name',
                  onChange: formik.handleChange,
                  value: formik.values.name
                }
              },
              {
                label: 'Your email',
                props: {
                  id: 'email',
                  name: 'email',
                  value: user?.email,
                  disabled: true
                }
              },
              {
                label: 'Correspondence email',
                props: {
                  id: 'correspondenceEmail',
                  name: 'correspondenceEmail',
                  value: formik.values.correspondenceEmail,
                  onChange: formik.handleChange,
                  subLabel:
                    'The default contact email used in all candidate conversations',
                  error: !!formik.errors.correspondenceEmail,
                  helperText: formik.errors.correspondenceEmail
                }
              }
            ]}
            customComponents={[
              <Box>
                {!isOnboardingPage && (
                  <Button
                    onClick={async () => {
                      await formik.submitForm();
                    }}
                    variant="primary"
                    disabled={formik.isSubmitting}
                  >
                    Save profile
                  </Button>
                )}
              </Box>
            ]}
          />
        </form>
      </Box>
      {isOnboardingPage && (
        <Footer
          isLoading={formik.isSubmitting}
          cancelText="Cancel"
          primaryText="Get started"
          primaryDisabled={!formik.isValid}
          onPrimaryClick={formik.submitForm}
        />
      )}
    </>
  );
};
